const { isEmpty, } = require('lodash');

const { planOptions, monthlySalesRanges, basketPriceRanges, } = require('./rakutenFeeRates');
const {
  planOptions: shopifyPlanOptions,
  billingCycleOptions: shopifyBillingCycleOptions,
} = require('./shopifyFeeRates');
const {
  DEFAULT_POINT_RATE, DEFAULT_AFFILIATE_RATE, DEFAULT_PAYMENT_RATE,
} = require('./yahooFeeRates');

const mallOptions = [
  { value: 'rakuten', label: '楽天' },
  { value: 'shopify', label: 'Shopify' },
  { value: 'yahoo', label: 'Yahoo!ショッピング' },
];

// Shopify / Yahoo は審査・検証中のため、当面は限られたユーザーにのみ表示する。
//   - user.admin           … サービス運営者
//   - user.isShopifyTester … Shopify の審査担当者用アカウント（DBで直接付与する）
// 一般公開する際は adminOnlyMalls から当該モールを外す。
//
// ★Yahoo は実接続の検証が未完了のうえ、固定IP(VPC/NAT)の整備も未了。
//   IP制限をかけているストアでは接続に失敗するため、検証が済むまで一般公開しない。
//   詳細は docs/static-ip.md / docs/yahoo-integration.md。
const adminOnlyMalls = ['shopify', 'yahoo'];

const canUseShopify = (user) => !!(user?.admin || user?.isShopifyTester);

const visibleMallOptions = (user) =>
  canUseShopify(user) ? mallOptions : mallOptions.filter(_ => !adminOnlyMalls.includes(_.value));

const fields = ({ user } = {}) => {
  return {
    mall: {
      type: 'select',
      label: 'モール',
      options: visibleMallOptions(user),
      validations: {
        required: v => !isEmpty(v),
      },
    },
    name: {
      type: 'string',
      label: '店舗名',
      validations: {
        required: v => !isEmpty(v),
      },
    },
    rakutenShopUrl: {
      type: 'string',
      label: 'ショップURL',
      hidden: state => state.mall !== 'rakuten',
      validations: {},
    },
    rakutenServiceSecret: {
      type: 'password',
      label: 'serviceSecret（RMS > WEB APIサービス > アプリ一覧）',
      hidden: state => state.mall !== 'rakuten',
      validations: {
        required: (v, state) => state.mall !== 'rakuten' || !isEmpty(v),
      },
    },
    rakutenLicenseKey: {
      type: 'password',
      label: 'ライセンスキー（RMS > WEB APIサービス > アプリ一覧）',
      hidden: state => state.mall !== 'rakuten',
      validations: {
        required: (v, state) => state.mall !== 'rakuten' || !isEmpty(v),
      },
    },
    rakutenPlan: {
      type: 'select',
      label: '出店プラン',
      options: planOptions,
      hidden: state => state.mall !== 'rakuten',
      validations: {},
    },
    rakutenMonthlySalesRange: {
      type: 'select',
      label: '月商レンジ',
      options: monthlySalesRanges.map(_ => ({ value: _.value, label: _.label })),
      hidden: state => state.mall !== 'rakuten',
      validations: {},
    },
    rakutenBasketPriceRange: {
      type: 'select',
      label: '平均バスケット単価',
      options: basketPriceRanges.map(_ => ({ value: _.value, label: _.label })),
      hidden: state => state.mall !== 'rakuten' || state.rakutenPlan === 'ganbare',
      validations: {},
    },
    // RPP 広告レポート取得用の RMS ログイン情報。暗号化して別コレクションに保存するため、
    // ここでは入力のみ受け取り、保存時に shop ドキュメントからは除去する（平文を残さない）。
    // 既存値は読み込まない（password 扱い）。変更時のみ入力。
    rppRLoginId: {
      type: 'string',
      label: '【広告】R-Login ID',
      hidden: state => state.mall !== 'rakuten',
      validations: {},
    },
    rppRLoginPassword: {
      type: 'password',
      label: '【広告】R-Login パスワード（90日ごとに要更新）',
      hidden: state => state.mall !== 'rakuten',
      // 保存済みなら桁数分の ● をプレースホルダ表示。空のまま保存すると変更なし扱い
      placeholder: state => state.rppRLoginPasswordLength ? '●'.repeat(state.rppRLoginPasswordLength) : '',
      validations: {},
    },
    rppMemberId: {
      type: 'string',
      label: '【広告】楽天会員 ID（メールアドレス）',
      hidden: state => state.mall !== 'rakuten',
      validations: {},
    },
    rppMemberPassword: {
      type: 'password',
      label: '【広告】楽天会員 パスワード',
      hidden: state => state.mall !== 'rakuten',
      placeholder: state => state.rppMemberPasswordLength ? '●'.repeat(state.rppMemberPasswordLength) : '',
      validations: {},
    },
    // 保存済みパスワードの桁数（プレースホルダの ● 表示用）。常に非表示の補助フィールド。
    rppRLoginPasswordLength: {
      type: 'integer',
      label: '',
      hidden: () => true,
      validations: {},
    },
    rppMemberPasswordLength: {
      type: 'integer',
      label: '',
      hidden: () => true,
      validations: {},
    },

    // --- Shopify ---
    // ストアの myshopify ドメイン。アプリのインストール時に自動設定されるため通常は入力不要だが、
    // 手動で直す場合に備えて編集可能にしておく。表示名を誤って入れると OAuth に進めないため
    // 正規表現で myshopify ドメイン形式を強制する。
    shopifyShop: {
      type: 'string',
      label: 'ストアドメイン（◯◯◯.myshopify.com）',
      hidden: state => state.mall !== 'shopify',
      hint: 'アプリのインストール時に自動で設定されるため、通常は入力不要です',
      validations: {
        myshopifyDomain: v => isEmpty(v) || /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/.test(v),
      },
    },
    shopifyPlan: {
      type: 'select',
      label: '料金プラン',
      options: shopifyPlanOptions,
      hidden: state => state.mall !== 'shopify',
      validations: {},
    },
    shopifyBillingCycle: {
      type: 'select',
      label: '支払いサイクル',
      options: shopifyBillingCycleOptions,
      hidden: state => state.mall !== 'shopify',
      validations: {},
    },
    // Shopify ペイメント以外（外部決済）を使っている場合に追加取引手数料を計上する
    shopifyUsesExternalPayment: {
      type: 'boolean',
      label: 'Shopifyペイメント以外の決済を利用している',
      hidden: state => state.mall !== 'shopify',
      validations: {},
    },
    // OAuth コールバックが自動保存する。UI からは触らせない（常に hidden）。
    // アクセストークン本体は shop ドキュメントには置かず、クライアントから読めない
    // shopifySecrets コレクションに暗号化して保存する（lib/shopifyCredentials.js）。
    shopifyAuthorizedAt: {
      type: 'date',
      label: '',
      hidden: () => true,
      validations: {},
    },

    // --- Yahoo!ショッピング ---
    // ★Yahoo はストアごとにアプリ登録する方式。
    //   Yahoo には Shopify のような「1つの公開アプリを審査に通せば全ストアで使える」
    //   枠組みが無く、API利用申請が「Client ID × ストアアカウント」単位で行われ、
    //   さらにストアクリエイタPro への従業員登録(ビジネスID)が前提になるため。
    //   → 各店舗が Yahoo デベロッパーネットワークでアプリを作り、その値を入力してもらう。
    //
    // Client Secret と公開鍵は秘密情報なので shop ドキュメントには保存せず、
    // onCall(saveYahooCredentials)経由で yahooSecrets に暗号化保存する。
    yahooStoreAccount: {
      type: 'string',
      label: 'ストアアカウント（ストアURLに含まれる文字列）',
      hidden: state => state.mall !== 'yahoo',
      hint: '例: https://store.shopping.yahoo.co.jp/xxxxx/ の xxxxx の部分',
      validations: {
        required: (v, state) => state.mall !== 'yahoo' || !isEmpty(v),
      },
    },
    yahooSellerId: {
      type: 'string',
      label: 'セラーID',
      hidden: state => state.mall !== 'yahoo',
      hint: '通常はストアアカウントと同じ値です',
      validations: {
        required: (v, state) => state.mall !== 'yahoo' || !isEmpty(v),
      },
    },
    yahooClientId: {
      type: 'string',
      label: 'Client ID（Yahoo!デベロッパーネットワークで発行）',
      hidden: state => state.mall !== 'yahoo',
      validations: {
        required: (v, state) => state.mall !== 'yahoo' || !isEmpty(v),
      },
    },
    yahooClientSecret: {
      type: 'password',
      label: 'Client Secret（Yahoo!デベロッパーネットワークで発行）',
      hidden: state => state.mall !== 'yahoo',
      // 保存済みなら ● を表示。空のまま保存すると変更なし扱い
      placeholder: state => state.yahooClientSecretLength ? '●'.repeat(state.yahooClientSecretLength) : '',
      validations: {},
    },
    // 公開鍵は PEM 形式の複数行テキスト。ストアクリエイターPro > 設定 > 暗号鍵管理 で発行する。
    // ★有効期限は発行から1年。切れると連携が止まるため期限も保持して事前に通知する
    yahooPublicKey: {
      type: 'text',
      label: '公開鍵（ストアクリエイターPro > 設定 > 暗号鍵管理）',
      hidden: state => state.mall !== 'yahoo',
      hint: '-----BEGIN PUBLIC KEY----- から -----END PUBLIC KEY----- までを貼り付けてください。有効期限は発行から1年です',
      placeholder: state => state.yahooPublicKeySaved ? '（登録済み。変更する場合のみ入力）' : '',
      validations: {},
    },
    yahooPublicKeyVersion: {
      type: 'string',
      label: '公開鍵バージョン',
      hidden: state => state.mall !== 'yahoo',
      hint: '暗号鍵管理画面に表示されている数字',
      validations: {},
    },
    yahooPublicKeyExpiresAt: {
      type: 'date',
      label: '公開鍵の有効期限',
      hidden: state => state.mall !== 'yahoo',
      hint: '期限が近づいたらお知らせします（発行から1年）',
      validations: {},
    },
    // 手数料率。ストアごとに設定が異なるため入力してもらう（未入力なら既定値で推定）
    yahooPointRate: {
      type: 'float',
      label: `ストアポイント原資（倍。既定 ${DEFAULT_POINT_RATE}倍 = 1%）`,
      hidden: state => state.mall !== 'yahoo',
      validations: {
        greaterThanOrEqualTo0: v => v == null || v >= 0,
      },
    },
    yahooAffiliateRate: {
      type: 'float',
      label: `アフィリエイト料率（%。既定 ${DEFAULT_AFFILIATE_RATE * 100}%）`,
      hidden: state => state.mall !== 'yahoo',
      validations: {
        greaterThanOrEqualTo0: v => v == null || v >= 0,
      },
    },
    yahooPaymentRate: {
      type: 'float',
      label: `決済サービス個別手数料（%。既定 ${DEFAULT_PAYMENT_RATE * 100}%）`,
      hidden: state => state.mall !== 'yahoo',
      validations: {
        greaterThanOrEqualTo0: v => v == null || v >= 0,
      },
    },
    // OAuth コールバックが自動保存する。UI からは触らせない（常に hidden）
    yahooAuthorizedAt: {
      type: 'date',
      label: '',
      hidden: () => true,
      validations: {},
    },
    yahooAuthError: {
      type: 'string',
      label: '',
      hidden: () => true,
      validations: {},
    },
    // 保存済み Client Secret の桁数 / 公開鍵の登録有無（プレースホルダ表示用）。常に非表示
    yahooClientSecretLength: {
      type: 'integer',
      label: '',
      hidden: () => true,
      validations: {},
    },
    yahooPublicKeySaved: {
      type: 'boolean',
      label: '',
      hidden: () => true,
      validations: {},
    },
  };
};

// OAuth で自動保存されるフィールド。通常の店舗編集(updateDoc)では書き込ませない。
// （フォーム送信値に紛れて null 上書きされ、連携が切れるのを防ぐため strip する）
const shopifyCredentialFieldNames = [
  'shopifyAuthorizedAt',
];

// Yahoo の秘密情報 + OAuth 自動保存フィールド。通常の店舗編集(updateDoc)では書き込ませない。
// 秘密情報は onCall(saveYahooCredentials)で yahooSecrets に暗号化保存し、
// 認証情報は OAuth コールバックが書くため、フォーム送信値の null で上書きされるのを防ぐ。
const yahooCredentialFieldNames = [
  'yahooClientSecret', 'yahooPublicKey', 'yahooPublicKeyVersion', 'yahooPublicKeyExpiresAt',
  'yahooClientSecretLength', 'yahooPublicKeySaved',
  'yahooAuthorizedAt', 'yahooAuthError',
];

// RPP 関連フィールド名。通常の店舗編集（updateDoc）では書き込まず onCall 経由でのみ更新する。
// （認証4項目 + 表示用ヒント。フォーム送信値に紛れて null 上書きされるのを防ぐため strip する）
const rppCredentialFieldNames = [
  'rppRLoginId', 'rppRLoginPassword', 'rppMemberId', 'rppMemberPassword',
  'rppRLoginPasswordLength', 'rppMemberPasswordLength',
];

exports.fields = fields;
exports.mallOptions = mallOptions;
exports.adminOnlyMalls = adminOnlyMalls;
exports.visibleMallOptions = visibleMallOptions;
exports.canUseShopify = canUseShopify;
exports.rppCredentialFieldNames = rppCredentialFieldNames;
exports.shopifyCredentialFieldNames = shopifyCredentialFieldNames;
exports.yahooCredentialFieldNames = yahooCredentialFieldNames;
